// Rate limiting utilities for authentication security
// Implements client-side rate limiting to prevent brute force attacks

interface RateLimitAttempt {
  timestamp: number;
  ip?: string;
  email?: string;
}

interface RateLimitConfig {
  maxAttempts: number;
  windowMs: number;
  blockDurationMs: number;
}

// Rate limiting configurations
export const RATE_LIMIT_CONFIGS = {
  login: {
    maxAttempts: 5,
    windowMs: 15 * 60 * 1000, // 15 minutes
    blockDurationMs: 30 * 60 * 1000, // 30 minutes
  },
  signup: {
    maxAttempts: 3,
    windowMs: 60 * 60 * 1000, // 1 hour
    blockDurationMs: 60 * 60 * 1000, // 1 hour
  },
  passwordReset: {
    maxAttempts: 3,
    windowMs: 60 * 60 * 1000, // 1 hour
    blockDurationMs: 60 * 60 * 1000, // 1 hour
  },
};

class RateLimiter {
  private attempts: Map<string, RateLimitAttempt[]> = new Map();
  private blocked: Map<string, number> = new Map();

  private getStorageKey(type: string, identifier: string): string {
    return `rate_limit_${type}_${identifier}`;
  }

  private getBlockedKey(type: string, identifier: string): string {
    return `rate_limit_blocked_${type}_${identifier}`;
  }

  private loadFromStorage(key: string): RateLimitAttempt[] {
    try {
      const stored = localStorage.getItem(key);
      if (!stored) return [];
      
      const attempts = JSON.parse(stored) as RateLimitAttempt[];
      const now = Date.now();
      
      // Filter out expired attempts
      return attempts.filter(attempt => now - attempt.timestamp < RATE_LIMIT_CONFIGS.login.windowMs * 2);
    } catch {
      return [];
    }
  }

  private saveToStorage(key: string, attempts: RateLimitAttempt[]): void {
    try {
      localStorage.setItem(key, JSON.stringify(attempts));
    } catch {
      // Ignore storage errors
    }
  }

  private loadBlockedFromStorage(key: string): number | null {
    try {
      const stored = localStorage.getItem(key);
      if (!stored) return null;
      
      const blockedUntil = parseInt(stored, 10);
      if (Date.now() > blockedUntil) {
        localStorage.removeItem(key);
        return null;
      }
      
      return blockedUntil;
    } catch {
      return null;
    }
  }

  private saveBlockedToStorage(key: string, blockedUntil: number): void {
    try {
      localStorage.setItem(key, blockedUntil.toString());
    } catch {
      // Ignore storage errors
    }
  }

  public isBlocked(type: keyof typeof RATE_LIMIT_CONFIGS, identifier: string): { blocked: boolean; remainingMs?: number } {
    const config = RATE_LIMIT_CONFIGS[type];
    const key = this.getStorageKey(type, identifier);
    const blockedKey = this.getBlockedKey(type, identifier);
    
    // Check if explicitly blocked
    const blockedUntil = this.loadBlockedFromStorage(blockedKey);
    if (blockedUntil) {
      const remainingMs = blockedUntil - Date.now();
      if (remainingMs > 0) {
        return { blocked: true, remainingMs };
      }
    }

    // Check rate limiting
    const attempts = this.loadFromStorage(key);
    const now = Date.now();
    const recentAttempts = attempts.filter(attempt => now - attempt.timestamp < config.windowMs);

    if (recentAttempts.length >= config.maxAttempts) {
      // Block the user
      const blockUntil = now + config.blockDurationMs;
      this.saveBlockedToStorage(blockedKey, blockUntil);
      return { blocked: true, remainingMs: config.blockDurationMs };
    }

    return { blocked: false };
  }

  public recordAttempt(type: keyof typeof RATE_LIMIT_CONFIGS, identifier: string, success: boolean = false): void {
    const key = this.getStorageKey(type, identifier);
    const attempts = this.loadFromStorage(key);
    
    // Add new attempt
    attempts.push({
      timestamp: Date.now(),
      email: identifier,
    });

    // If successful, clear attempts
    if (success) {
      this.saveToStorage(key, []);
      const blockedKey = this.getBlockedKey(type, identifier);
      localStorage.removeItem(blockedKey);
    } else {
      this.saveToStorage(key, attempts);
    }
  }

  public getRemainingAttempts(type: keyof typeof RATE_LIMIT_CONFIGS, identifier: string): number {
    const config = RATE_LIMIT_CONFIGS[type];
    const key = this.getStorageKey(type, identifier);
    const attempts = this.loadFromStorage(key);
    const now = Date.now();
    
    const recentAttempts = attempts.filter(attempt => now - attempt.timestamp < config.windowMs);
    return Math.max(0, config.maxAttempts - recentAttempts.length);
  }

  public getTimeUntilReset(type: keyof typeof RATE_LIMIT_CONFIGS, identifier: string): number {
    const config = RATE_LIMIT_CONFIGS[type];
    const key = this.getStorageKey(type, identifier);
    const attempts = this.loadFromStorage(key);
    
    if (attempts.length === 0) return 0;
    
    const oldestRecentAttempt = Math.min(...attempts.map(a => a.timestamp));
    const resetTime = oldestRecentAttempt + config.windowMs;
    
    return Math.max(0, resetTime - Date.now());
  }

  public clearAttempts(type: keyof typeof RATE_LIMIT_CONFIGS, identifier: string): void {
    const key = this.getStorageKey(type, identifier);
    const blockedKey = this.getBlockedKey(type, identifier);
    localStorage.removeItem(key);
    localStorage.removeItem(blockedKey);
  }
}

// Export singleton instance
export const rateLimiter = new RateLimiter();

// Utility functions
export function formatRemainingTime(ms: number): string {
  const minutes = Math.ceil(ms / (1000 * 60));
  if (minutes === 1) return '1 minute';
  if (minutes < 60) return `${minutes} minutes`;
  
  const hours = Math.ceil(minutes / 60);
  if (hours === 1) return '1 hour';
  return `${hours} hours`;
}

export function getRateLimitMessage(type: keyof typeof RATE_LIMIT_CONFIGS, remainingMs: number): string {
  const timeText = formatRemainingTime(remainingMs);
  
  switch (type) {
    case 'login':
      return `Too many login attempts. Please try again in ${timeText}.`;
    case 'signup':
      return `Too many signup attempts. Please try again in ${timeText}.`;
    case 'passwordReset':
      return `Too many password reset attempts. Please try again in ${timeText}.`;
    default:
      return `Rate limit exceeded. Please try again in ${timeText}.`;
  }
}